<template>
  <div>
    <!-- Text-and-Button:start -->
    <div class="flex items-baseline justify-between mt-10 relative">
      <h2 class="intro-y text-lg font-medium -top-1">
        {{ $t(`${viewRoot}.list`) }}
      </h2>
      <button type="button" class="btn btn-primary w-40 md:w-34 h-8" @click="newPlatform">
        <span>{{ $t(`${viewRoot}.new`) }}</span>
      </button>
    </div>
    <!-- Text-and-Button:end -->

    <!-- Main-container:start -->
    <div class="grid grid-cols-12 gap-6 mb-12">
      <!--Header:start-->
      <div class="intro-y col-span-12 flex flex-col sm:flex-row items-center justify-between mt-2 gap-4">
        <div class="w-full sm:w-auto mr-auto">
          <p>{{ $t(`${viewRoot}.description`) }}</p>
        </div>

        <div class="w-full sm:w-auto relative text-gray-700 dark:text-gray-300">
          <input
            type="text"
            class="form-control w-full sm:w-72 box pr-10 placeholder-theme-13"
            :placeholder="$t(`${viewRoot}.search_text`)"
            v-model="searchInput"
            @keyup="filterPlatforms"
          />
          <SearchIcon class="w-4 h-4 absolute my-auto inset-y-0 mr-3 right-0" />
        </div>
      </div>
      <!-- Header:end -->

      <!-- Data-List:start -->
      <div class="intro-y col-span-12 overflow-auto">
        <table class="table table-report -mt-2 overflow-hidden">
          <thead>
            <tr>
              <th class="whitespace-nowrap">ID</th>
              <th class="whitespace-nowrap">{{ $t(`${viewRoot}.table.title1`) }}</th>
              <th class="whitespace-nowrap">{{ $t(`${viewRoot}.table.region`) }}</th>
              <th class="whitespace-nowrap">{{ $t(`${viewRoot}.table.link`) }}</th>
              <th class="text-center whitespace-nowrap">{{ $t(`${viewRoot}.table.actions`) }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="platform in platforms" :key="platform.id">
              <tr class="intro-x">
                <td>{{ platform.id }}</td>
                <td>{{ platform.name }}</td>
                <td>
                  <span
                    class="px-2 py-1 rounded text-xs font-semibold border-1"
                    :class="getRegionClass(platform.region_of_origin)"
                  >
                    {{ getRegionName(platform.region_of_origin) }}
                  </span>
                </td>
                <td class="max-w-xs truncate">
                  <a :href="platform.link_page" target="_blank" class="text-white-500 underline break-all">
                    {{ platform.link_page }}
                  </a>
                </td>
                <td>
                  <div class="flex justify-center items-center">
                    <a class="flex items-center mr-3" href="javascript:;" @click="editPlatform(platform.id)">
                      <CheckSquareIcon class="w-4 h-4 mr-1" />
                      {{ $t("views.edit") }}
                    </a>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Data-List:end -->
    </div>
    <!-- Main-container:end -->

    <!-- Pagination:start -->
    <PaginationControls
      v-model:page="pagination.page"
      v-model:per_page="pagination.per_page"
      :total_pages="pagination.total_pages"
      :total="pagination.total"
      :sizes="[3, 10, 25, 35, 50]"
      @request="getPlatforms"
    >
      <template #summary>
        {{ $t("components.pagination.viewing", { total: platforms.length }) }}
      </template>
    </PaginationControls>
    <!-- Pagination:end -->
  </div>
</template>

<script>
import ScienceSourceService from "@/services/science-source.service";
import PaginationControls from "@/components/pagination-control/Main.vue";

export default {
  name: "ScienceSourcePlatformsList",
  components: {
    PaginationControls,
  },
  data() {
    return {
      platforms: [],
      viewRoot: "views.science_source_platforms",
      searchInput: "",
      debounceTimeout: null,
      pagination: {
        page: 0,
        total_pages: 0,
        per_page: 10,
        total: 0,
      },
    };
  },
  mounted() {
    this.getPlatforms();
  },
  methods: {
    async getPlatforms() {
      const params = {
        search: this.searchInput || "",
        pagination: this.pagination,
      };
      try {
        const response = await ScienceSourceService.getAll(params);
        if (response && response.data) {
          this.platforms = response.data;
          this.paginationSetData(response.pagination);
        } else {
          this.platforms = Array.isArray(response) ? response : [];
          this.paginationSetData({
            page: 0,
            per_page: this.platforms.length,
            total: this.platforms.length,
            total_pages: 1,
          });
        }
      } catch (e) {
        this.platforms = [];
      }
    },
    filterPlatforms() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.pagination.page = 0;
        this.getPlatforms();
      }, 1000);
    },
    newPlatform() {
      this.$router.push({ path: "/science-source-platforms-new" });
    },
    editPlatform(id) {
      this.$router.push({ path: "/science-source-platforms-edit", query: { id } });
    },

    // Data
    getRegionName(region) {
      const regionName = region?.toUpperCase() ?? "";

      if (regionName == "") return "";

      const translationKey = `views.science_source_platforms.regions.${regionName}`;
      const translatedText = this.$t(translationKey);

      if (translatedText !== translationKey) {
        return `${translatedText}`;
      }

      // Fallback
      const capitalRegionName = regionName.charAt(0).toUpperCase() + regionName.slice(1).toLowerCase();
      return capitalRegionName;
    },

    // Styles
    getRegionClass(region) {
      switch (region) {
        case "WESTERN":
          return "border-gray-500 text-gray-500";
        case "EASTERN":
          return "border-gray-500 text-gray-500";
        default:
          return "border-red-500 text-red-500";
      }
    },

    // Pagination
    paginationSetData({ page = 0, per_page: perPage = 0, total = 0, total_pages: totalPages = 0 } = {}) {
      this.pagination.page = page;
      this.pagination.per_page = perPage;
      this.pagination.total = total;
      this.pagination.total_pages = totalPages;
    },
  },
};
</script>
